import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../utils/api';

// ─── Mini Modal ───────────────────────────────────────────────────────────────
const Modal = ({ title, onClose, children }) => (
  <div className="modal-overlay">
    <div className="modal" style={{ maxWidth: 640, width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h3 className="modal-title" style={{ margin: 0 }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '1.5rem', lineHeight: 1 }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

const StarDisplay = ({ value, max = 5 }) => (
  <span style={{ color: '#f59e0b', letterSpacing: 2 }}>
    {'★'.repeat(value)}
    <span style={{ color: 'var(--border-secondary)' }}>{'★'.repeat(max - value)}</span>
  </span>
);

// ─── Tab: Pertanyaan ─────────────────────────────────────────────────────────
const QuestionsTab = ({ token }) => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);  // null | 'create' | question_obj
  const [form, setForm] = useState({ question_text: '', description: '', is_active: true, order: 0 });

  const fetch_ = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/survey-questions');
      setQuestions(Array.isArray(data) ? data : []);
    } catch (err) { toast.error('Gagal memuat pertanyaan: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch_(); }, []);

  const openCreate = () => {
    setForm({ question_text: '', description: '', is_active: true, order: questions.length + 1 });
    setModal('create');
  };

  const openEdit = (q) => {
    setForm({ question_text: q.question_text, description: q.description || '', is_active: q.is_active, order: q.order });
    setModal(q);
  };

  const handleSave = async () => {
    if (!form.question_text.trim()) { toast.error('Teks pertanyaan wajib diisi.'); return; }
    const isEdit = modal && modal !== 'create';
    const url = isEdit ? `/admin/survey-questions/${modal.id}` : '/admin/survey-questions';
    try {
      const { data } = isEdit ? await api.put(url, form) : await api.post(url, form);
      toast.success(data.message);
      setModal(null);
      fetch_();
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus pertanyaan ini? Semua jawaban terkait juga akan terhapus.')) return;
    try {
      const { data } = await api.delete(`/admin/survey-questions/${id}`);
      toast.success(data.message);
      fetch_();
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          {questions.length} pertanyaan tersedia. Pertanyaan aktif akan tampil pada form survei publik.
        </p>
        <button onClick={openCreate} className="btn btn-primary">+ Tambah Pertanyaan</button>
      </div>

      <div className="card table-container">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Memuat...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 50 }}>#</th>
                <th>Pertanyaan</th>
                <th>Deskripsi</th>
                <th>Status</th>
                <th>Urutan</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {questions.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
                  Belum ada pertanyaan. Klik "+ Tambah Pertanyaan" untuk mulai.
                </td></tr>
              ) : questions.map(q => (
                <tr key={q.id}>
                  <td style={{ fontWeight: 700, color: 'var(--accent-primary)' }}>#{q.id}</td>
                  <td style={{ maxWidth: 280 }}>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.4 }}>{q.question_text}</div>
                  </td>
                  <td style={{ maxWidth: 200, color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                    {q.description || <span style={{ color: 'var(--text-tertiary)' }}>-</span>}
                  </td>
                  <td>
                    {q.is_active
                      ? <span className="badge badge-green">Aktif</span>
                      : <span className="badge badge-red">Nonaktif</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>{q.order}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => openEdit(q)} className="btn btn-secondary btn-sm">Edit</button>
                      <button onClick={() => handleDelete(q.id)} className="btn btn-danger btn-sm">Hapus</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <Modal title={modal === 'create' ? 'Tambah Pertanyaan Baru' : 'Edit Pertanyaan'} onClose={() => setModal(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label className="form-label">Teks Pertanyaan <span style={{ color: 'var(--error)' }}>*</span></label>
              <input type="text" className="form-input" value={form.question_text}
                onChange={e => setForm({ ...form, question_text: e.target.value })}
                placeholder="Contoh: Bagaimana kualitas layanan helpdesk kami?" />
            </div>
            <div className="form-group">
              <label className="form-label">Deskripsi / Keterangan</label>
              <input type="text" className="form-input" value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Opsional" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group">
                <label className="form-label">Urutan Tampil</label>
                <input type="number" className="form-input" value={form.order} min={0}
                  onChange={e => setForm({ ...form, order: parseInt(e.target.value) })} />
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-input" value={form.is_active ? '1' : '0'}
                  onChange={e => setForm({ ...form, is_active: e.target.value === '1' })}>
                  <option value="1">Aktif (Tampil di form publik)</option>
                  <option value="0">Nonaktif (Sembunyikan)</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
              <button className="btn btn-secondary" onClick={() => setModal(null)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave}>Simpan</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ─── Tab: Respons Pengguna ───────────────────────────────────────────────────
const ResponsesTab = ({ token }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);     // null | response_obj
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editAnswers, setEditAnswers] = useState({});
  const [replyText, setReplyText] = useState('');

  const fetch_ = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/survey-responses');
      setResponses(Array.isArray(data) ? data : []);
    } catch (err) { toast.error('Gagal memuat respons: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch_(); }, []);

  const openDetail = (r) => {
    setSelected(r);
    setEditMode(false);
    setReplyText(r.respon_ahli || '');
    const answersMap = {};
    (r.answers || []).forEach(a => { answersMap[a.question_id] = a.score; });
    setEditAnswers(answersMap);
    setEditForm({ user_name: r.user_name, instansi: r.instansi || '', saran_pengguna: r.saran_pengguna || '' });
  };

  const handleReply = async () => {
    try {
      const { data } = await api.put(`/admin/survey-responses/${selected.id}/reply`, { respon_ahli: replyText });
      toast.success(data.message);
      setResponses(prev => prev.map(x => x.id === selected.id ? { ...x, respon_ahli: replyText, status: replyText ? 'ditanggapi' : 'baru' } : x));
      setSelected(null);
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
  };

  const handleEdit = async () => {
    try {
      const { data } = await api.put(`/admin/survey-responses/${selected.id}`, { ...editForm, answers: editAnswers });
      toast.success(data.message);
      fetch_();
      setSelected(null);
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus respons survei ini secara permanen?')) return;
    try {
      const { data } = await api.delete(`/admin/survey-responses/${id}`);
      toast.success(data.message);
      setResponses(prev => prev.filter(x => x.id !== id));
    } catch (err) { toast.error(err.response?.data?.error || err.message); }
  };

  const avgScore = (r) => {
    if (!r.answers || r.answers.length === 0) return null;
    return (r.answers.reduce((s, a) => s + a.score, 0) / r.answers.length).toFixed(1);
  };

  return (
    <div>
      <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginBottom: 16 }}>
        {responses.length} respons masuk. Klik "Detail/Balas" untuk melihat jawaban lengkap dan memberi respon ahli.
      </p>

      <div className="card table-container">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>Memuat...</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Responden</th>
                <th>Rerata</th>
                <th>Status</th>
                <th style={{ maxWidth: 200 }}>Saran Pengguna</th>
                <th>Tanggal</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {responses.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-tertiary)' }}>
                  Belum ada respons survei.
                </td></tr>
              ) : responses.map(r => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.user_name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>{r.instansi || '-'}</div>
                  </td>
                  <td>
                    {avgScore(r) ? (
                      <span className="badge badge-orange">★ {avgScore(r)}</span>
                    ) : <span style={{ color: 'var(--text-tertiary)' }}>-</span>}
                  </td>
                  <td>
                    {r.status === 'ditanggapi'
                      ? <span className="badge badge-green">Ditanggapi</span>
                      : <span className="badge badge-red">Menunggu</span>}
                  </td>
                  <td style={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
                    {r.saran_pengguna || <span style={{ color: 'var(--text-tertiary)' }}>-</span>}
                  </td>
                  <td style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                    {new Date(r.created_at).toLocaleDateString('id-ID')}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => openDetail(r)} className="btn btn-secondary btn-sm">Detail / Balas</button>
                      <button onClick={() => handleDelete(r.id)} className="btn btn-danger btn-sm">Hapus</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <Modal title={editMode ? `✏️ Edit Respons — ${selected.user_name}` : `📋 Detail Respons — ${selected.user_name}`} onClose={() => setSelected(null)}>
          {!editMode ? (
            // View / Reply mode
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ background: 'var(--bg-tertiary)', padding: 16, borderRadius: 'var(--radius-md)' }}>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                  <strong>Responden:</strong> {selected.user_name} — {selected.instansi || '-'}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  <strong>Tanggal:</strong> {new Date(selected.created_at).toLocaleString('id-ID')}
                </div>
              </div>

              {/* Jawaban per pertanyaan */}
              {(selected.answers || []).length > 0 && (
                <div>
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 8 }}>Jawaban Penilaian:</p>
                  {selected.answers.map(a => (
                    <div key={a.id} style={{ marginBottom: 10, padding: '10px 14px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                        {a.question?.question_text || `Pertanyaan #${a.question_id}`}
                      </div>
                      <StarDisplay value={a.score} />
                      <span style={{ marginLeft: 8, fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>
                        {['', 'Sangat Buruk', 'Buruk', 'Cukup', 'Baik', 'Sangat Baik'][a.score]}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {selected.saran_pengguna && (
                <div style={{ background: 'var(--bg-tertiary)', padding: '14px', borderRadius: 'var(--radius-md)', borderLeft: '3px solid var(--info)' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--info)', marginBottom: 4 }}>SARAN PENGGUNA</p>
                  <p style={{ fontStyle: 'italic', color: 'var(--text-primary)', margin: 0 }}>"{selected.saran_pengguna}"</p>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Respon Ahli / Balasan Admin</label>
                <textarea className="form-textarea" rows={4} value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Tulis balasan resmi kepada pengguna ini..." />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setEditMode(true)}>✏️ Edit Data</button>
                <button className="btn btn-primary" onClick={handleReply}>💾 Simpan Balasan</button>
              </div>
            </div>
          ) : (
            // Edit mode
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Nama Responden</label>
                  <input type="text" className="form-input" value={editForm.user_name}
                    onChange={e => setEditForm({ ...editForm, user_name: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Instansi</label>
                  <input type="text" className="form-input" value={editForm.instansi}
                    onChange={e => setEditForm({ ...editForm, instansi: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Saran Pengguna</label>
                <textarea className="form-textarea" rows={3} value={editForm.saran_pengguna}
                  onChange={e => setEditForm({ ...editForm, saran_pengguna: e.target.value })} />
              </div>
              {(selected.answers || []).length > 0 && (
                <div>
                  <label className="form-label" style={{ marginBottom: 10 }}>Nilai Bintang Per Pertanyaan</label>
                  {selected.answers.map(a => (
                    <div key={a.id} style={{ marginBottom: 10 }}>
                      <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 4 }}>
                        {a.question?.question_text || `Pertanyaan #${a.question_id}`}
                      </p>
                      <select className="form-input" value={editAnswers[a.question_id] || a.score}
                        onChange={e => setEditAnswers({ ...editAnswers, [a.question_id]: parseInt(e.target.value) })}>
                        {[1, 2, 3, 4, 5].map(s => (
                          <option key={s} value={s}>{s} ★ — {['', 'Sangat Buruk', 'Buruk', 'Cukup', 'Baik', 'Sangat Baik'][s]}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setEditMode(false)}>← Kembali</button>
                <button className="btn btn-primary" onClick={handleEdit}>💾 Simpan Perubahan</button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────
const AdminSurveysPage = () => {
  const [activeTab, setActiveTab] = useState('responses');
  const token = localStorage.getItem('kopeng_token');

  const tabs = [
    { id: 'responses', label: '📋 Respon Pengguna' },
    { id: 'questions', label: '❓ Kelola Pertanyaan' },
  ];

  return (
    <div style={{ padding: '0 0 48px' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontWeight: 700, fontSize: '1.25rem', color: 'var(--text-primary)', marginBottom: 4 }}>
          Survey Kepuasan Masyarakat
        </h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Kelola pertanyaan kuesioner dan pantau respon kepuasan layanan LPSE Kabupaten Ngada.
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', padding: 4, width: 'fit-content' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 20px', borderRadius: 'var(--radius-sm)', border: 'none', cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 700 : 500, fontSize: '0.875rem',
              background: activeTab === tab.id ? 'var(--accent-primary)' : 'transparent',
              color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
              transition: 'all 0.2s'
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'responses' && <ResponsesTab token={token} />}
      {activeTab === 'questions' && <QuestionsTab token={token} />}
    </div>
  );
};

export default AdminSurveysPage;
